import AsyncStorage from '@react-native-async-storage/async-storage';
import { Alert } from 'react-native';
import type { StorageKey } from 'app/lib/storageKeys';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
export * from './storageKeys';

export const storage = {
  clear,
  multiClear,
  readString,
  saveString,
};

async function readString(storageKey: StorageKey): Promise<string | null> {
  try {
    return await AsyncStorage.getItem(storageKey);
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error);
    return null;
  }
}

async function saveString(storageKey: StorageKey, value?: string): Promise<void> {
  if (!value) {
    if (value === undefined) {
      await clear(storageKey);
      return;
    } else {
      throw Error(`Aucune valeur à sauvegarder pour key=${storageKey}, value=${value}`);
    }
  }
  try {
    await AsyncStorage.setItem(storageKey, value);
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error);
  }
}

async function clear(storageKey: StorageKey): Promise<void> {
  try {
    await AsyncStorage.removeItem(storageKey);
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error);
  }
}

async function multiClear(storageKeys: StorageKey[]): Promise<void> {
  try {
    await AsyncStorage.multiRemove(storageKeys);
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error);
  }
}

function onAsyncStorageError(error: Error | undefined): void {
  const stringifiedError = JSON.stringify(error);
  Alert.alert('AsyncStorage Error', stringifiedError);
}

export function useStoredValue2<T>(
  key: StorageKey,
  defaultValue?: T,
  state?: [T | undefined, Dispatch<SetStateAction<T | undefined>>],
  filterValuesUsingDefaultValues = false,
  waitUntil = true,
) {
  const [value, updateValue, usedDefault, prefLoaded] = useStoredValue(
    key,
    defaultValue,
    state,
    filterValuesUsingDefaultValues,
    waitUntil,
  );
  return { value, updateValue, usedDefault, prefLoaded };
}

export function useStoredValue<T>(
  key: StorageKey,
  defaultValue?: T,
  state?: [T | undefined, Dispatch<SetStateAction<T | undefined>>],
  filterValuesUsingDefaultValues = false,
  waitUntil = true,
) {
  const newState = useState<T | undefined>(undefined);

  const resolvedState = state ?? newState;
  const [value, setValue] = resolvedState;

  const [prefLoaded, setPrefLoaded] = useState(false);
  const [usedDefault, setUsedDefault] = useState(false);

  // Load on mount and whenever the default changes (for example, after login).
  useEffect(() => {
    if (!waitUntil) return;
    let active = true;
    async function getPreference() {
      const storedPreference = await storage.readString(key);
      const storedValue = storedPreference ? (JSON.parse(storedPreference) as T) : defaultValue;
      if (
        filterValuesUsingDefaultValues &&
        Array.isArray(storedValue) &&
        Array.isArray(defaultValue) &&
        defaultValue.length > 0
      ) {
        const allowedValues = new Set(defaultValue);
        return {
          usedDefault: !Boolean(storedPreference),
          value: storedValue.filter((id) => allowedValues.has(id)) as T,
        };
      }
      return { usedDefault: !Boolean(storedPreference), value: storedValue };
    }
    void getPreference().then((preference) => {
      if (!active) return;
      setUsedDefault(preference.usedDefault);
      setValue(preference.value);
      setPrefLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [defaultValue, filterValuesUsingDefaultValues, key, setValue, waitUntil]);

  const updateValue = ((newValue: T) => {
    setValue(newValue);
    void storage.saveString(key, JSON.stringify(newValue));
  }) as Dispatch<SetStateAction<T | undefined>>;

  return [value, updateValue, usedDefault, prefLoaded] as [
    T | undefined,
    Dispatch<SetStateAction<T>>,
    boolean,
    boolean,
  ];
}
